// @catalog-federation — certificate requests (D8)
// ============================================================================
// The TypeScript twin of libs/federation/CertificateRequests.cs. A node's setup
// writes a request for its keys; the organization's ceremony console
// countersigns it once the operator is bound; the file travels to the
// Developer, whose `cl-root certify` runs the same checks as
// {@link checkCertificateRequest}.
//
//   • Proof of possession: every certified key signs
//     {@link certificateRequestSigningBytes} (`service_certificate_request.v1`)
//     — hybrid keys with the hybrid profile, the `jwt` key with ES256.
//   • Countersignature: the bound organization's `main_signing_key` signs
//     {@link certificateCountersignatureBytes}
//     (`service_certificate_countersignature.v1`), which embeds the request.
//   • A request file is the RFC 8785 bytes of
//     `{ request, proofs: { name → signature }, organization? }`; a
//     re-formatted file is refused.

import { canonicalize } from '@catalog-canonical-json';
import {
    type JsonObject, FederationFileError, StrictObject, dictionary, hasOwn, parseCanonical, toBase64,
} from './json';
import { type SignatureVerifier, type SigningProfile } from './signatures';
import { type CertifiedKeys, certifiedKeysJson, readCertifiedKeys } from './certified-keys';
import { isDefinedServiceKind, isOperatorCode, isOrganizationCid } from './records';

export const CERTIFICATE_REQUEST_TYPE = 'service_certificate_request.v1';
export const CERTIFICATE_COUNTERSIGNATURE_TYPE = 'service_certificate_countersignature.v1';

/** What a node asks to have certified. `kind` is `cpr` or `cid`; the keys are
 *  exactly the kind's (`certifiedKeyNames`). */
export interface CertificateRequest {
    operatorId: string;
    kind: string;
    nodeUuid: string;
    keys: CertifiedKeys;
    /** RFC 3339 UTC to the second. */
    requestedAt: string;
}

/** The organization's countersignature: its CID and a signature over
 *  {@link certificateCountersignatureBytes}. The key is not in the file — it
 *  is the key of the operator's binding. */
export interface OrganizationCountersignature {
    orgCid: string;
    signature: Uint8Array;
}

/** A request file: the request, one proof per certified key, and the
 *  countersignature once the organization has added it. */
export interface SignedCertificateRequest {
    request: CertificateRequest;
    proofs: Readonly<Record<string, Uint8Array>>;
    organization: OrganizationCountersignature | null;
}

/** The key a countersignature is checked against: the operator's current binding (D8). */
export interface OrganizationKey {
    orgCid: string;
    publicKey: Uint8Array;
    signingProfile: SigningProfile;
}

export const CertificateRequestRefusals = {
    Malformed: 'malformed',
    MissingProof: 'missing_proof',
    BadProof: 'bad_proof',
    OrganizationRequired: 'organization_required',
    OperatorUnbound: 'operator_unbound',
    OrganizationMismatch: 'organization_mismatch',
    BadOrganizationSignature: 'bad_organization_signature',
} as const;

export type CertificateRequestRefusalCode = (typeof CertificateRequestRefusals)[keyof typeof CertificateRequestRefusals];

/** Why a request may not be signed. Codes are stable; the detail is for the person at the ceremony. */
export interface CertificateRequestRefusal {
    code: CertificateRequestRefusalCode;
    detail: string;
}

// ── payloads ────────────────────────────────────────────────────────────

/**
 * The request as it is signed and filed. Throws {@link FederationFileError}
 * (`malformed`) when a field is not what `cl-root` accepts, so nothing is
 * signed that the Developer would refuse.
 */
export function certificateRequestPayload(r: CertificateRequest): JsonObject {
    const payload: JsonObject = {
        keys: certifiedKeysJson(r.keys),
        kind: r.kind,
        node_uuid: r.nodeUuid,
        operator_id: r.operatorId,
        requested_at: r.requestedAt,
        type: CERTIFICATE_REQUEST_TYPE,
    };
    readRequestPayload(StrictObject.of(payload, 'certificate request'));
    return payload;
}

/** The bytes every certified key signs (proof of possession). */
export function certificateRequestSigningBytes(r: CertificateRequest): Uint8Array {
    return canonicalize(certificateRequestPayload(r));
}

/** What the organization countersigns: the whole request under its own discriminator. */
export function certificateCountersignaturePayload(r: CertificateRequest, orgCid: string): JsonObject {
    if (!isOrganizationCid(orgCid)) {
        throw new FederationFileError('malformed', `countersignature: '${orgCid}' is not an organization CID`);
    }
    return {
        org_cid: orgCid,
        request: certificateRequestPayload(r),
        type: CERTIFICATE_COUNTERSIGNATURE_TYPE,
    };
}

/** The bytes the organization's `main_signing_key` signs. */
export function certificateCountersignatureBytes(r: CertificateRequest, orgCid: string): Uint8Array {
    return canonicalize(certificateCountersignaturePayload(r, orgCid));
}

// ── files ───────────────────────────────────────────────────────────────

/** The canonical bytes of a request file — what setup writes and the console
 *  downloads once it has added the countersignature. */
export function certificateRequestFile(s: SignedCertificateRequest): Uint8Array {
    const proofs = dictionary<JsonObject[string]>();
    for (const [name, signature] of Object.entries(s.proofs)) proofs[name] = toBase64(signature);
    const file: JsonObject = {
        proofs,
        request: certificateRequestPayload(s.request),
    };
    if (s.organization !== null) {
        file['organization'] = {
            org_cid: s.organization.orgCid,
            signature: toBase64(s.organization.signature),
        };
    }
    const bytes = canonicalize(file);
    readCertificateRequestFile(bytes); // what is written must read back
    return bytes;
}

/**
 * Reads a delivered request file: canonical bytes and a well-formed request
 * for a defined kind. Signatures are not checked here — see
 * {@link checkCertificateRequest}. Throws {@link FederationFileError}
 * (`non_canonical`, `malformed`).
 */
export function readCertificateRequestFile(file: string | Uint8Array): SignedCertificateRequest {
    const o = StrictObject.of(parseCanonical(file, 'certificate request file'), 'certificate request file');
    const request = readRequestPayload(o.object('request'));

    const proofsObject = o.object('proofs');
    const proofs = dictionary<Uint8Array>();
    for (const name of proofsObject.names()) proofs[name] = proofsObject.base64(name);
    proofsObject.end();

    let organization: OrganizationCountersignature | null = null;
    if (o.has('organization')) {
        const org = o.object('organization');
        const orgCid = org.string('org_cid');
        if (!isOrganizationCid(orgCid)) throw org.fail(`'org_cid' is not an organization CID: '${orgCid}'`);
        organization = { orgCid, signature: org.base64('signature') };
        org.end();
    }
    o.end();
    return { request, proofs, organization };
}

// ── the checks cl-root certify runs ─────────────────────────────────────

/**
 * Null when the request may be signed, else the refusal (C# `Check`, D8, R3):
 *   • every certified key has a proof that verifies against it, and no proof
 *     names a key the request does not certify;
 *   • `boundOrganization` is the key of the operator's current binding, or
 *     null when the operator is not bound. A bound operator's request must
 *     carry that organization's countersignature, and it must verify; an
 *     unbound operator's request must carry none (bind before certify).
 */
export async function checkCertificateRequest(
    s: SignedCertificateRequest,
    boundOrganization: OrganizationKey | null,
    signatures: SignatureVerifier,
): Promise<CertificateRequestRefusal | null> {
    const request = s.request;
    const signingBytes = certificateRequestSigningBytes(request);
    for (const [name, key] of Object.entries(request.keys)) {
        const proof = hasOwn(s.proofs, name) ? s.proofs[name] : undefined;
        if (proof === undefined) {
            return refusal('missing_proof', `no proof of possession for the '${name}' key`);
        }
        if (!(await signatures.verify(signingBytes, proof, key.publicKey, key.signingProfile))) {
            return refusal('bad_proof', `the proof for the '${name}' key does not verify against that key`);
        }
    }
    for (const name of Object.keys(s.proofs)) {
        if (!hasOwn(request.keys, name)) {
            return refusal('malformed', `a proof for '${name}', which the request does not certify`);
        }
    }

    if (boundOrganization === null) {
        return s.organization === null
            ? null
            : refusal('operator_unbound', `the request is countersigned by ${s.organization.orgCid}, `
                + `but operator ${request.operatorId} is not bound; sign the binding first`);
    }
    if (s.organization === null) {
        return refusal('organization_required',
            `operator ${request.operatorId} is bound to ${boundOrganization.orgCid}; its countersignature is required`);
    }
    if (s.organization.orgCid !== boundOrganization.orgCid) {
        return refusal('organization_mismatch', `countersigned by ${s.organization.orgCid}, `
            + `but operator ${request.operatorId} is bound to ${boundOrganization.orgCid}`);
    }
    const countersigned = await signatures.verify(
        certificateCountersignatureBytes(request, s.organization.orgCid), s.organization.signature,
        boundOrganization.publicKey, boundOrganization.signingProfile);
    return countersigned
        ? null
        : refusal('bad_organization_signature', "the organization countersignature does not verify against the binding's key");
}

function refusal(code: CertificateRequestRefusalCode, detail: string): CertificateRequestRefusal {
    return { code, detail };
}

/** Reads a `service_certificate_request.v1` payload, `type` included; ends the object. */
function readRequestPayload(r: StrictObject): CertificateRequest {
    if (r.string('type') !== CERTIFICATE_REQUEST_TYPE) throw r.fail(`'type' must be ${CERTIFICATE_REQUEST_TYPE}`);
    const operatorId = r.string('operator_id');
    if (!isOperatorCode(operatorId)) {
        throw r.fail(`'operator_id' is not an operator code (two to four of a-z, 0-9): '${operatorId}'`);
    }
    const kind = r.string('kind');
    if (!isDefinedServiceKind(kind)) throw r.fail(`kind '${kind}' has no key schema in this release`);
    const nodeUuid = r.uuid('node_uuid');
    const keys = readCertifiedKeys(r.object('keys'), kind);
    const requestedAt = r.timestamp('requested_at');
    r.end();
    return { operatorId, kind, nodeUuid, keys, requestedAt };
}
